// =====================================================
// SIMPLIFIED CONTACT SERVICE
// =====================================================
// Handles basic contact CRUD operations only
// No more complex inheritance or type-specific logic
// =====================================================

import { supabase } from '@/utils/supabase';
import logger from '@/utils/logger';
import type {
  Contact,
  ContactFilters,
  CreateContactRequest,
  ReferralPartner
} from '@/types/contacts-new';

export class ContactService {
  // =====================================================
  // BASIC CRUD OPERATIONS
  // =====================================================
  
  /**
   * Create a new contact (borrower or referral partner)
   */
  async create(data: CreateContactRequest): Promise<Contact> {
    // Get user's organization
    const { data: profile, error: profileError } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', (await supabase.auth.getUser()).data.user?.id)
      .single();

    if (profileError || !profile) {
      throw new Error('User profile not found');
    }

    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) {
throw new Error('User not authenticated');
}

    // Create contact
    const { data: contact, error } = await supabase
      .from('contacts_simple')
      .insert({
        organization_id: profile.organization_id,
        contact_type: data.contactType,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        mobile_phone: data.mobilePhone,
        mailing_street: data.mailingStreet,
        mailing_city: data.mailingCity,
        mailing_state: data.mailingState,
        mailing_zip: data.mailingZip,
        source: data.originalSource || 'direct',
        assigned_loan_officer_id: data.assignedLoanOfficerId || userId,
        tags: data.tags || [],
        notes: data.notes,
        opt_in_marketing: true,
        opt_in_sms: false
      })
      .select()
      .single();

    if (error) {
      // Handle duplicate email error
      if (error.code === '23505' && error.message.includes('unique_email_per_org')) {
        throw new Error('A contact with this email already exists in your organization');
      }
      throw error;
    }

    return this.mapToContact(contact);
  }

  /**
   * Get contact by ID
   */
  async getById(id: string): Promise<Contact | null> {
    const { data, error } = await supabase
      .from('contacts_simple')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
return null;
} // Not found
      throw error;
    }

    return this.mapToContact(data);
  }

  /**
   * Update contact
   */
  async update(id: string, updates: Partial<Contact>): Promise<Contact> {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) {
throw new Error('User not authenticated');
}

    const { data, error } = await supabase
      .from('contacts_simple')
      .update({
        first_name: updates.firstName,
        last_name: updates.lastName,
        middle_name: updates.middleName,
        suffix: updates.suffix,
        preferred_name: updates.preferredName,
        email: updates.email,
        mobile_phone: updates.mobilePhone,
        home_phone: updates.homePhone,
        work_phone: updates.workPhone,
        preferred_contact_method: updates.preferredContactMethod,
        mailing_street: updates.mailingStreet,
        mailing_unit: updates.mailingUnit,
        mailing_city: updates.mailingCity,
        mailing_state: updates.mailingState,
        mailing_zip: updates.mailingZip,
        assigned_loan_officer_id: updates.assignedLoanOfficerId,
        tags: updates.tags,
        notes: updates.notes,
        opt_in_marketing: updates.optInMarketing,
        opt_in_sms: updates.optInSms
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
throw error;
}
    return this.mapToContact(data);
  }

  /**
   * Soft delete contact (available to all users)
   * Sets deleted_at timestamp to hide from views without actually removing data
   */
  async softDelete(id: string, reason?: string): Promise<{ already_deleted?: boolean }> {
    try {
      // Use the improved soft delete RPC function
      const { data, error } = await supabase.rpc('soft_delete_contact', {
        p_contact_id: id,
        p_reason: reason
      });

      if (error) {
        logger.error('Soft delete contact error:', error);
        throw new Error(error.message || 'Failed to remove contact');
      }

      if (data && !data.success) {
        throw new Error(data.error || 'Failed to remove contact');
      }

      // Return whether the contact was already deleted
      return { already_deleted: data?.already_deleted || false };
    } catch (error: ServiceError | null) {
      logger.error('Soft delete error:', error);
      throw error;
    }
  }

  /**
   * Restore a soft-deleted contact (admin only)
   */
  async restore(id: string, reason?: string): Promise<void> {
    const { data, error } = await supabase.rpc('restore_contact', {
      p_contact_id: id,
      p_reason: reason
    });

    if (error) {
      logger.error('Restore contact error:', error);
      throw new Error(error.message || 'Failed to restore contact');
    }

    if (data && !data.success) {
      logger.error('Restore contact failed:', data);
      throw new Error(data.error || 'Failed to restore contact');
    }
  }

  /**
   * Get delete impact - shows what will be deleted
   */
  async getDeleteImpact(contactId: string): Promise<{
    contact: Contact;
    leadCount: number;
    applicationCount: number;
    borrowerCount: number;
    totalValue: number;
  }> {
    // Get contact details
    const contact = await this.getById(contactId);
    if (!contact) {
throw new Error('Contact not found');
}

    // Count related records
    const [leads, applications, borrowers] = await Promise.all([
      supabase.from('leads').select('id', { count: 'exact', head: true }).eq('contact_id', contactId),
      supabase.from('applications').select('id', { count: 'exact', head: true }).eq('contact_id', contactId),
      supabase.from('borrowers').select('id, funded_amount').eq('contact_id', contactId)
    ]);

    // Calculate total value
    const totalValue = borrowers.data?.reduce((sum, b) => sum + (b.funded_amount || 0), 0) || 0;

    return {
      contact,
      leadCount: leads.count || 0,
      applicationCount: applications.count || 0,
      borrowerCount: borrowers.count || 0,
      totalValue
    };
  }

  /**
   * List contacts with filters
   */
  async list(filters: ContactFilters = {}): Promise<Contact[]> {
    let query = supabase
      .from('contacts_simple')
      .select('*')
      .is('deleted_at', null)  // Filter out soft-deleted records
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters.contactType) {
      query = query.eq('contact_type', filters.contactType);
    }

    if (filters.search) {
      query = query.or(
        `first_name.ilike.%${filters.search}%,` +
        `last_name.ilike.%${filters.search}%,` +
        `email.ilike.%${filters.search}%`
      );
    }

    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    if (filters.assignedLoanOfficerId) {
      query = query.eq('assigned_loan_officer_id', filters.assignedLoanOfficerId);
    }

    if (filters.createdAfter) {
      query = query.gte('created_at', filters.createdAfter);
    }

    if (filters.createdBefore) {
      query = query.lte('created_at', filters.createdBefore);
    }

    const { data, error } = await query;

    if (error) {
throw error;
}
    return (data || []).map(d => this.mapToContact(d));
  }


  // =====================================================
  // HELPER METHODS
  // =====================================================
  
  /**
   * Check if email exists in organization (excludes soft-deleted contacts)
   * Uses SECURITY DEFINER RPC function to bypass RLS for org-wide duplicate checking
   */
  async emailExists(email: string, excludeId?: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('check_email_exists_in_org', {
      p_email: email,
      p_exclude_id: excludeId || null
    });

    if (error) {
      logger.error('Error checking email existence:', error);
      return false;
    }

    return data === true;
  }

  /**
   * Check if a phone number already exists for a contact in the organization
   * Uses SECURITY DEFINER RPC function to bypass RLS for org-wide duplicate checking
   * Normalizes phone to last 10 digits for comparison
   */
  async phoneExists(phone: string, excludeId?: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('check_phone_exists_in_org', {
      p_phone: phone,
      p_exclude_id: excludeId || null
    });

    if (error) {
      logger.error('Error checking phone existence:', error);
      return false;
    }

    return data === true;
  }

  /**
   * Map database record to Contact type
   */
  private mapToContact(data: any): Contact {
    return {
      id: data.id,
      organizationId: data.organization_id,
      contactType: data.contact_type,
      firstName: data.first_name,
      lastName: data.last_name,
      middleName: data.middle_name,
      suffix: data.suffix,
      preferredName: data.preferred_name,
      dateOfBirth: data.date_of_birth,
      ssnLast4: data.ssn_last4,
      email: data.email,
      mobilePhone: data.mobile_phone,
      homePhone: data.home_phone,
      workPhone: data.work_phone,
      preferredContactMethod: data.preferred_contact_method,
      mailingStreet: data.mailing_street,
      mailingUnit: data.mailing_unit,
      mailingCity: data.mailing_city,
      mailingState: data.mailing_state,
      mailingZip: data.mailing_zip,
      originalSource: data.source,
      optInMarketing: data.opt_in_marketing,
      optInSms: data.opt_in_sms,
      assignedLoanOfficerId: data.assigned_loan_officer_id,
      tags: data.tags || [],
      notes: data.notes,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  }

}

// Export singleton instance
export const contactService = new ContactService();